import { z } from "zod";

/**
 * THE DEMO DOMAIN, and it exists to be deleted.
 *
 * It is here so the boiler proves every layer end to end on day one — guard, validation,
 * role-based view, query, converter, store, screen, an upload and a push. A boiler whose
 * patterns are only described is a boiler whose patterns are wrong.
 *
 * `/setup` offers to remove it once a real domain exists.
 */
export const noteSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  /** Storage key, not a URL: signed read URLs expire and must never be persisted. */
  imageKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const createNoteSchema = z.object({
  title: z.string().min(1, "required").max(120),
  body: z.string().max(4000).default(""),
  imageKey: z.string().nullable().optional(),
});

export const updateNoteSchema = createNoteSchema.partial().extend({ id: z.string() });

/**
 * `coerce`, because this schema validates BOTH a JSON body and a query string, and a query
 * string has no numbers in it — `?limit=2` arrives as "2". Without it the same contract
 * accepts a payload and rejects the identical request as a query, which is a confusing way
 * to spend an afternoon.
 */
/**
 * THE PAGE SIZE, and the ceiling on one.
 *
 * `default` is what the app actually gets: `notes.service.ts` sends a cursor and no limit,
 * so this number IS the app's page size. It was 20, which is the low end of two phone
 * screens — fine behind a "load more" button, and far too small once the list loads by
 * being scrolled: a single flick outruns the page and the list waits on the network in
 * front of the user. 50 rows is around five screens, and measured on the wire it is 19kB
 * for a typical note (title ~40, body ~180 characters) against 7kB for 20.
 *
 * `max` is a different thing and does NOT go away with the client-side limit that used to
 * justify it. It is the bound that keeps one request from becoming an outage — the same rule
 * `framework/data-layer` states for batch reads ("a bounded maximum so the batch cannot
 * become the outage"). Raised 50 → 200 because the list can now render that many, and
 * stopped there: at the contract's own worst case (a 120-character title and the full
 * 4,000-character body on every row) 200 rows is an 836kB response, and 500 would be 2MB
 * over a phone radio.
 */
export const listNotesSchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const noteListSchema = z.object({
  items: z.array(noteSchema),
  nextCursor: z.string().nullable(),
});

/**
 * The id in `/v1/notes/:id`. Shared rather than declared in the route, so the app rejects a
 * malformed id before the round trip and both sides mean the same thing by "an id".
 */
export const noteIdSchema = z.object({ id: z.uuid() });

/**
 * What someone opening a shared link sees: the note without its body. A second, genuinely
 * different shape rather than a description of one — the view that renders it is what proves
 * the contract is the field whitelist.
 */
export const notePublicSummarySchema = noteSchema.pick({ id: true, title: true, createdAt: true });

export type NoteDTO = z.infer<typeof noteSchema>;
export type NoteIdParam = z.infer<typeof noteIdSchema>;
export type CreateNotePayload = z.infer<typeof createNoteSchema>;
export type UpdateNotePayload = z.infer<typeof updateNoteSchema>;
export type NoteListDTO = z.infer<typeof noteListSchema>;
export type NotePublicSummaryDTO = z.infer<typeof notePublicSummarySchema>;
